/**
 * Publish View Page — `/p/:slug`
 *
 * Public-facing form runner for a published Knod.
 *
 * Layout (post-unlock):
 *   ┌────────────────────────────────────────────────────────────────────┐
 *   │ [Knods logo]   Title              [← Publishes] [UserMenu]         │
 *   ├──────────────────────────────┬─────────────────────────────────────┤
 *   │ INPUTS                       │ OUTPUTS                             │
 *   │  - text fields               │  Grid of all results from this      │
 *   │  - file drops                │  session, newest first              │
 *   │  ─────────────────────────   │                                     │
 *   │  Run [count: 1▲] [Run]       │  Click a tile → fullscreen Lightbox │
 *   └──────────────────────────────┴─────────────────────────────────────┘
 *
 * Run mechanics:
 *   - The Inputs panel stays mounted across runs.
 *   - Run kicks off N parallel /run requests (one per stepper count).
 *   - Each batch member is polled independently and appended to a flat
 *     `runHistory` array as it completes.
 *   - Outputs panel renders the history as a grid; click a tile to open
 *     the existing Lightbox.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import JSZip from 'jszip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LoginIcon from '@mui/icons-material/Login';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { MediaPreview } from '../components/ui/MediaPreview';
import { generateMediaFilename } from '../utils/markdownToRtf';
import { Lightbox } from '../components/ui/Lightbox';
import { KnodsIconLogo } from '../components/ui/KnodsIconLogo';
import { UserMenu } from '../components/UserMenu';
import { useCredits } from '../contexts/CreditsContext';
import { supabase } from '../lib/supabase';
import { toastService } from '../components/ui/Toast';
import logger from '../utils/logger';
import {
  PublishMetadata,
  InputSchemaEntry,
  OutputSchemaEntry,
  RunStatus,
  getPublishMetadata,
  unlockPublish,
  uploadPublishFile,
  startPublishRun,
  getPublishRunStatus,
} from '../services/PublishService';
import './PublishViewPage.css';

type Stage = 'loading' | 'locked' | 'unlocking' | 'ready' | 'error';

interface InputValue {
  /** For text: the typed string. For media: the uploaded URL (after upload). */
  content: string;
  fileName?: string;
  uploading?: boolean;
}

/**
 * One materialized output tile in the grid. Many of these per run when there
 * are multiple outputs, and many runs in a session = many tiles.
 */
interface OutputTile {
  /** Stable id for React keys */
  id: string;
  /** Which run produced it */
  runId: string;
  /** Schema label this came from (e.g. "final composition") */
  label: string;
  /** Tile media */
  type: 'image' | 'video' | 'audio' | 'text' | 'unknown';
  url?: string;
  text?: string;
  raw?: any;
  /** When the run completed */
  completedAt: string;
}

/**
 * Tracks one in-flight run in the batch (for the running indicator).
 */
interface InFlightRun {
  runId: string;
  status: 'pending' | 'running';
  progress?: { completed: number; total: number };
  /**
   * How many output tiles this run is expected to ultimately produce. Used to
   * size the progressive-reveal skeletons: we render (expected − arrived)
   * skeletons and hide them once arrived ≥ expected. A full run expects the
   * whole output schema; a regenerate expects exactly one.
   */
  expectedOutputs: number;
}

const POLL_INTERVAL_MS = 1500;
const MAX_BATCH = 10;

const PublishViewPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  // Live balance — visitors are signed in (owner-only access today), so the
  // existing CreditsContext is populated and updates in realtime as runs charge.
  const { credits } = useCredits();

  const [stage, setStage] = useState<Stage>('loading');
  const [metadata, setMetadata] = useState<PublishMetadata | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  // Non-fatal banner for partial runs ('completed_with_errors'): lists which
  // outputs failed/were skipped while still rendering the produced ones.
  const [partialWarning, setPartialWarning] = useState<string | null>(null);

  // post-unlock state
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [inputs, setInputs] = useState<InputSchemaEntry[]>([]);
  const [outputs, setOutputs] = useState<OutputSchemaEntry[]>([]);
  const [values, setValues] = useState<Record<string, InputValue>>({});
  // Server-computed cost-per-run (set on /unlock). Multiplied by runCount in the UI.
  const [costPerRun, setCostPerRun] = useState<number>(0);

  // batch run state
  const [runCount, setRunCount] = useState<number>(1);
  const [inFlightRuns, setInFlightRuns] = useState<InFlightRun[]>([]);
  const [runHistory, setRunHistory] = useState<OutputTile[]>([]);
  const pollTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // lightbox
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // "Download all" zip-in-progress state
  const [zipProgress, setZipProgress] = useState<{ done: number; total: number } | null>(null);
  const isZipping = zipProgress !== null;

  // ─────────────────────────────────────────────────────────────────────────
  // Initial load + auto-unlock
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setStage('loading');
    getPublishMetadata(slug)
      .then((meta) => {
        if (cancelled) return;
        setMetadata(meta);
        setTitle(meta.title);
        setDescription(meta.description);
        setStage('locked');
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorMessage(err.message || 'Not found');
        setStage('error');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Auto-attempt unlock with the visitor's existing session
  useEffect(() => {
    if (stage !== 'locked' || !metadata || !slug) return;
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (session?.access_token) {
        attemptUnlock(session.access_token);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, metadata, slug]);

  // Cleanup poll timers on unmount
  useEffect(() => {
    return () => {
      for (const t of pollTimers.current.values()) clearTimeout(t);
      pollTimers.current.clear();
    };
  }, []);

  const attemptUnlock = async (accountToken: string) => {
    if (!slug) return;
    setStage('unlocking');
    setErrorMessage(null);
    try {
      const result = await unlockPublish(slug, accountToken);
      setSessionToken(result.sessionToken);
      setTitle(result.title);
      setDescription(result.description);
      setInputs(result.inputs);
      setOutputs(result.outputs);
      setCostPerRun(result.estimatedCostPerRun || 0);

      const initialValues: Record<string, InputValue> = {};
      for (const entry of result.inputs) {
        initialValues[entry.nodeId] = { content: '' };
      }
      setValues(initialValues);

      setStage('ready');
    } catch (err: any) {
      setErrorMessage(err.message || 'Unlock failed');
      setStage('locked');
    }
  };

  const handleSignInRedirect = () => {
    const back = encodeURIComponent(window.location.pathname);
    window.location.href = `/?redirect=${back}`;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Inputs
  // ─────────────────────────────────────────────────────────────────────────

  const handleFileChange = async (nodeId: string, file: File | null) => {
    if (!file || !slug || !sessionToken) return;
    setValues((prev) => ({
      ...prev,
      [nodeId]: { content: '', fileName: file.name, uploading: true },
    }));
    try {
      const result = await uploadPublishFile(slug, sessionToken, file);
      setValues((prev) => ({
        ...prev,
        [nodeId]: { content: result.url, fileName: file.name, uploading: false },
      }));
    } catch (err: any) {
      setValues((prev) => ({
        ...prev,
        [nodeId]: { content: '', fileName: file.name, uploading: false },
      }));
      setRunError(err.message || 'Upload failed');
    }
  };

  const canSubmit = useMemo(() => {
    return inputs.every((entry) => {
      if (!entry.required) return true;
      const v = values[entry.nodeId];
      if (!v) return false;
      if (v.uploading) return false;
      return !!v.content.trim();
    });
  }, [inputs, values]);

  // ─────────────────────────────────────────────────────────────────────────
  // Run mechanics — batch + accumulate
  // ─────────────────────────────────────────────────────────────────────────

  const buildPayload = useCallback(() => {
    return inputs.map((entry) => ({
      nodeId: entry.nodeId,
      content: values[entry.nodeId]?.content || '',
      type: entry.type,
    }));
  }, [inputs, values]);

  const handleRun = async () => {
    if (!slug || !sessionToken || !canSubmit) return;
    setRunError(null);
    setPartialWarning(null);

    const payload = buildPayload();
    const count = Math.max(1, Math.min(MAX_BATCH, runCount));

    // Kick off N parallel /run requests
    const startPromises: Promise<{ runId: string }>[] = [];
    for (let i = 0; i < count; i++) {
      startPromises.push(startPublishRun(slug, sessionToken, payload));
    }

    let started: { runId: string }[];
    try {
      started = await Promise.all(startPromises);
    } catch (err: any) {
      setRunError(err.message || 'Failed to start run');
      return;
    }

    // Add them to the in-flight list and start polling each
    setInFlightRuns((prev) => [
      ...prev,
      ...started.map((s) => ({
        runId: s.runId,
        status: 'pending' as const,
        expectedOutputs: Math.max(1, outputs.length),
      })),
    ]);

    for (const s of started) {
      pollRun(s.runId);
    }
  };

  // Regenerate just one output: re-runs the ancestor subgraph of that output
  const handleRegenerate = async (label: string) => {
    if (!slug || !sessionToken) return;
    setRunError(null);
    setPartialWarning(null);

    const payload = buildPayload();

    let started: { runId: string };
    try {
      started = await startPublishRun(slug, sessionToken, payload, { targetOutputKey: label });
    } catch (err: any) {
      setRunError(err.message || 'Failed to start regeneration');
      return;
    }

    setInFlightRuns((prev) => [
      ...prev,
      { runId: started.runId, status: 'pending' as const, expectedOutputs: 1 },
    ]);
    pollRun(started.runId);
  };

  /**
   * Build a human-readable list of which outputs did not produce for a partial
   * run. Derived from status.nodeStates: any node with status 'failed' or
   * 'skipped'. We map nodeId → output label via the output schema. Caveat: the
   * label↔nodeId reverse map is clean only when apiOutput flags are present;
   * for sink-fallback runs the output label IS the nodeId, so we fall back to
   * matching on label too.
   */
  const describeFailedOutputs = (status: RunStatus): string => {
    const ns = status.nodeStates || {};
    const deadNodeIds = Object.keys(ns).filter(
      (id) => ns[id]?.status === 'failed' || ns[id]?.status === 'skipped',
    );

    const names = new Set<string>();
    for (const out of outputs) {
      // apiOutput-flagged: out.nodeId is the real node id. Sink-fallback:
      // out.label === out.nodeId, so either match catches it.
      if (deadNodeIds.includes(out.nodeId) || deadNodeIds.includes(out.label)) {
        names.add(out.label);
      }
    }

    const labelList = Array.from(names);
    const failedCount = Object.values(ns).filter((s: any) => s?.status === 'failed').length;
    if (labelList.length > 0) {
      return `Some outputs could not be generated: ${labelList.join(', ')}. The rest are shown below.`;
    }
    // No output node mapped (e.g. an intermediate node failed) — generic message.
    return failedCount > 0
      ? 'Some steps failed; partial results are shown below.'
      : 'This run finished with some skipped steps; partial results are shown below.';
  };

  const pollRun = useCallback(
    async (runId: string) => {
      if (!slug || !sessionToken) return;
      try {
        const status = await getPublishRunStatus(slug, sessionToken, runId);

        // Update in-flight progress
        setInFlightRuns((prev) =>
          prev.map((r) =>
            r.runId === runId
              ? { ...r, status: status.status as 'pending' | 'running', progress: status.progress }
              : r,
          ),
        );

        if (status.status === 'completed') {
          appendOutputs(runId, status);
          removeInFlight(runId);
          return;
        }
        if (status.status === 'completed_with_errors') {
          // Partial success: render whatever outputs were produced, then show a
          // NON-fatal banner naming the failed/skipped outputs.
          appendOutputs(runId, status);
          removeInFlight(runId);
          setPartialWarning(describeFailedOutputs(status));
          return;
        }
        if (status.status === 'failed' || status.status === 'cancelled') {
          // Cancelled runs may still have flushed partial outputs server-side —
          // reveal them before tearing the run down.
          if (status.outputs && Object.keys(status.outputs).length > 0) {
            appendOutputs(runId, status);
          }
          // Surface the error inline; still remove from in-flight
          setRunError(status.error?.message || `Run ${status.status}`);
          removeInFlight(runId);
          return;
        }

        // PROGRESSIVE REVEAL: while still running, merge in any partial outputs
        // the server has flushed for completed topological levels so far. Merge
        // is idempotent (upsert by output identity) so repeating this every poll
        // is cheap and stable.
        if (status.outputs && Object.keys(status.outputs).length > 0) {
          appendOutputs(runId, status);
        }

        // Schedule next poll
        const t = setTimeout(() => pollRun(runId), POLL_INTERVAL_MS);
        pollTimers.current.set(runId, t);
      } catch (err: any) {
        setRunError(err.message || 'Polling failed');
        removeInFlight(runId);
      }
    },
    [slug, sessionToken],
  );

  const removeInFlight = (runId: string) => {
    setInFlightRuns((prev) => prev.filter((r) => r.runId !== runId));
    const t = pollTimers.current.get(runId);
    if (t) {
      clearTimeout(t);
      pollTimers.current.delete(runId);
    }
  };

  /**
   * Merge the outputs from a poll into the tile grid. Idempotent + incremental:
   * safe to call on EVERY poll (running or terminal). For each output present in
   * the response we upsert a tile keyed by OUTPUT IDENTITY (the schema label,
   * falling back to nodeId) so that:
   *  - polling the same running run repeatedly does not duplicate tiles (we
   *    update in place if the url changed, e.g. raw→proxied);
   *  - a regenerate (which starts a NEW runId) REPLACES the prior tile for that
   *    output identity instead of stacking a duplicate.
   */
  const appendOutputs = (runId: string, status: RunStatus) => {
    const outputData = status.outputs || {};
    const parsed: OutputTile[] = [];

    for (const entry of outputs) {
      const data = outputData[entry.label] ?? outputData[entry.nodeId];
      if (!data) continue;

      const type: OutputTile['type'] =
        typeof data === 'object' && data?.type
          ? (data.type as any)
          : typeof data === 'string'
          ? 'text'
          : 'unknown';
      const url: string | undefined =
        typeof data === 'object'
          ? data?.url || data?.imageUrl || data?.videoUrl || data?.audioUrl
          : undefined;
      const text: string | undefined =
        typeof data === 'string'
          ? data
          : typeof data === 'object'
          ? data?.content || data?.text
          : undefined;

      parsed.push({
        // Stable across runs: keyed by output identity, NOT runId. This makes
        // regenerate replace-in-place and per-poll merges idempotent.
        id: `out:${entry.label || entry.nodeId}`,
        runId,
        label: entry.label,
        type,
        url,
        text,
        raw: data,
        completedAt: status.completedAt || new Date().toISOString(),
      });
    }

    if (parsed.length === 0) return;

    setRunHistory((prev) => {
      // Upsert by output identity: newly-arrived/updated tiles go to the front
      // (newest first); existing tiles not in this batch keep their prior order.
      const touched = new Set(parsed.map((t) => t.id));
      const back = prev.filter((t) => !touched.has(t.id));
      return [...parsed, ...back];
    });
  };

  const handleClearHistory = () => {
    setRunHistory([]);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Downloads
  // ─────────────────────────────────────────────────────────────────────────

  const downloadTile = useCallback(async (tile: OutputTile) => {
    if (!tile.url) return;
    const mediaType = tile.type === 'video' ? 'video' : 'image';
    try {
      // Fetch as blob to ensure same-origin download (avoids browser preview behavior)
      const res = await fetch(tile.url);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = generateMediaFilename(mediaType, { url: tile.url, nodeTitle: tile.label });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      // Fallback: direct link without blob
      const link = document.createElement('a');
      link.href = tile.url;
      link.download = generateMediaFilename(mediaType, { url: tile.url, nodeTitle: tile.label });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, []);

  /**
   * Bundle every currently-visible downloadable tile (snapshot of `runHistory`
   * at click time) into a single client-side ZIP and trigger one download.
   *
   * Why a ZIP instead of N sequential downloads? Browsers throttle/block
   * many programmatic downloads in quick succession, even when staggered.
   * One blob → one download is reliable across browsers and popup blockers.
   *
   * Per-file failures are tolerated: we skip the file, increment a counter,
   * and surface the result in a single summary toast at the end.
   */
  const downloadAll = useCallback(async () => {
    // Snapshot of "what's on screen right now" — same filter the tile grid uses.
    const downloadable = runHistory.filter(
      (t) => (t.type === 'image' || t.type === 'video') && t.url,
    );

    if (downloadable.length === 0) {
      toastService.info('Nothing to download');
      return;
    }

    const zip = new JSZip();
    const total = downloadable.length;
    setZipProgress({ done: 0, total });

    // Track filename collisions inside the zip (two tiles with same label/ext).
    const usedNames = new Set<string>();
    const dedupe = (name: string): string => {
      if (!usedNames.has(name)) {
        usedNames.add(name);
        return name;
      }
      const dot = name.lastIndexOf('.');
      const base = dot >= 0 ? name.slice(0, dot) : name;
      const ext = dot >= 0 ? name.slice(dot) : '';
      let i = 2;
      while (usedNames.has(`${base}-${i}${ext}`)) i++;
      const out = `${base}-${i}${ext}`;
      usedNames.add(out);
      return out;
    };

    let failed = 0;
    let added = 0;

    for (let i = 0; i < downloadable.length; i++) {
      const tile = downloadable[i];
      const mediaType = tile.type === 'video' ? 'video' : 'image';
      try {
        const res = await fetch(tile.url!);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const filename = dedupe(
          generateMediaFilename(mediaType, {
            url: tile.url,
            nodeTitle: tile.label,
            mimeType: blob.type || undefined,
            pageIndex: i,
          }),
        );
        zip.file(filename, blob);
        added++;
      } catch (err) {
        failed++;
        logger.warn('[PublishView] Failed to fetch tile for zip', {
          tileId: tile.id,
          url: tile.url,
          error: err instanceof Error ? err.message : String(err),
        });
      }
      setZipProgress({ done: i + 1, total });
    }

    if (added === 0) {
      setZipProgress(null);
      toastService.error(`Failed to fetch any of ${total} files. Nothing to download.`);
      return;
    }

    try {
      const blob = await zip.generateAsync({ type: 'blob' });
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const stamp =
        `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
        `-${pad(now.getHours())}${pad(now.getMinutes())}`;
      const zipName = `knods-${slug || 'flow'}-${stamp}.zip`;

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = zipName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);

      if (failed > 0) {
        toastService.warning(`Downloaded ${added} of ${total} files. ${failed} failed to fetch.`);
      } else {
        toastService.success(`Downloaded ${added} file${added === 1 ? '' : 's'}.`);
      }
    } catch (err) {
      logger.warn('[PublishView] Failed to generate zip', err);
      toastService.error('Failed to build ZIP archive.');
    } finally {
      setZipProgress(null);
    }
  }, [runHistory, slug]);

  // ─────────────────────────────────────────────────────────────────────────
  // Lightbox helpers
  // ─────────────────────────────────────────────────────────────────────────

  // Only image/video/audio tiles are openable in the lightbox; build a flat
  // gallery so prev/next navigation skips text tiles cleanly.
  const galleryTiles = useMemo(
    () => runHistory.filter((t) => (t.type === 'image' || t.type === 'video' || t.type === 'audio') && t.url),
    [runHistory],
  );

  const openLightbox = (tileId: string) => {
    const idx = galleryTiles.findIndex((t) => t.id === tileId);
    if (idx >= 0) setLightboxIndex(idx);
  };

  const closeLightbox = () => setLightboxIndex(null);
  const lightboxPrev = () =>
    setLightboxIndex((i) => (i === null || i <= 0 ? i : i - 1));
  const lightboxNext = () =>
    setLightboxIndex((i) => (i === null || i >= galleryTiles.length - 1 ? i : i + 1));

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  // Header is consistent across all post-loading states.
  const renderHeader = () => (
    <header className="publish-view__header">
      <div className="publish-view__header-left">
        <button
          type="button"
          className="publish-view__logo-btn"
          onClick={() => navigate('/')}
          aria-label="Go to dashboard"
          title="Dashboard"
        >
          <KnodsIconLogo className="publish-view__logo" />
        </button>
        <div className="publish-view__header-title">
          <h1 className="publish-view__title">{title || 'Untitled Knod'}</h1>
          {description && (
            <p className="publish-view__description">{description}</p>
          )}
        </div>
      </div>
      <div className="publish-view__header-right">
        <Tooltip title="Back to My Publishes">
          <button
            type="button"
            className="publish-view__header-link"
            onClick={() => navigate('/publishes')}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            <span>Publishes</span>
          </button>
        </Tooltip>
        <UserMenu creditsTooltipMode="buy-only" />
      </div>
    </header>
  );

  // ── Loading ────────────────────────────────────────────────────────────
  if (stage === 'loading') {
    return (
      <div className="publish-view">
        <div className="publish-view__center">
          <CircularProgress sx={{ color: 'var(--color-accent-primary)' }} />
        </div>
      </div>
    );
  }

  // ── Hard error (no metadata) ────────────────────────────────────────────
  if (stage === 'error' && !metadata) {
    return (
      <div className="publish-view">
        <div className="publish-view__center">
          <div className="publish-view__card">
            <h2 className="publish-view__card-title">Not found</h2>
            <p className="publish-view__hint">
              {errorMessage || "This published Knod doesn't exist or has been revoked."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Sign-in gate ────────────────────────────────────────────────────────
  if (stage === 'locked' || stage === 'unlocking') {
    return (
      <div className="publish-view">
        <div className="publish-view__center">
          <div className="publish-view__card">
            <h2 className="publish-view__card-title">
              <LoginIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
              Sign in required
            </h2>
            <p className="publish-view__hint">
              {title ? `"${title}" is private to its owner. Sign in to view it.` : "This Knod is private to its owner. Sign in to view it."}
            </p>
            {errorMessage && (
              <div className="publish-view__alert publish-view__alert--error">
                <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                <span>{errorMessage}</span>
              </div>
            )}
            <div className="publish-view__actions">
              <button
                type="button"
                className="publish-view__btn publish-view__btn--primary"
                onClick={handleSignInRedirect}
                disabled={stage === 'unlocking'}
              >
                {stage === 'unlocking' ? 'Unlocking…' : 'Sign in'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Ready: two-column layout ────────────────────────────────────────────
  const isRunning = inFlightRuns.length > 0;
  const completedCount = runHistory.length;
  const lightboxOpen = lightboxIndex !== null && galleryTiles[lightboxIndex];

  return (
    <div className="publish-view">
      {renderHeader()}

      <main className="publish-view__main">
        {/* ─────────────────────── INPUTS PANEL ─────────────────────── */}
        <section className="publish-view__panel publish-view__panel--inputs">
          <header className="publish-view__panel-header">
            <h2 className="publish-view__panel-title">Inputs</h2>
          </header>

          <div className="publish-view__panel-body">
            {inputs.length === 0 ? (
              <p className="publish-view__hint">
                This Knod has no inputs — just hit Run.
              </p>
            ) : (
              <div className="publish-view__form">
                {inputs.map((entry) => {
                  const v = values[entry.nodeId] || { content: '' };
                  if (entry.type === 'text') {
                    return (
                      <div key={entry.nodeId} className="publish-view__field">
                        <label className="publish-view__label">
                          {entry.label}
                          {entry.required && <span className="publish-view__required">*</span>}
                        </label>
                        <textarea
                          className="publish-view__textarea"
                          value={v.content}
                          onChange={(e) =>
                            setValues((prev) => ({
                              ...prev,
                              [entry.nodeId]: { content: e.target.value },
                            }))
                          }
                          placeholder={`Enter ${entry.label.toLowerCase()}…`}
                        />
                      </div>
                    );
                  }
                  const accept =
                    entry.type === 'image'
                      ? 'image/*'
                      : entry.type === 'video'
                      ? 'video/*'
                      : 'audio/*';
                  return (
                    <div key={entry.nodeId} className="publish-view__field">
                      <label className="publish-view__label">
                        {entry.label}
                        {entry.required && <span className="publish-view__required">*</span>}
                      </label>
                      <div
                        className={`publish-view__drop ${v.content ? 'publish-view__drop--has-file' : ''}`}
                      >
                        <UploadFileIcon className="publish-view__drop-icon" sx={{ fontSize: 28 }} />
                        <div className="publish-view__drop-text">
                          {v.uploading ? (
                            'Uploading…'
                          ) : v.content ? (
                            <>
                              <strong>{v.fileName || 'File uploaded'}</strong> — click to replace
                            </>
                          ) : (
                            <>
                              <strong>Click to upload</strong> or drag a {entry.type} here
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          accept={accept}
                          onChange={(e) => handleFileChange(entry.nodeId, e.target.files?.[0] || null)}
                        />
                      </div>
                      {v.content && entry.type === 'image' && (
                        <div className="publish-view__file-preview">
                          <MediaPreview type="image" url={v.content} maxHeight="180px" />
                        </div>
                      )}
                      {v.content && entry.type === 'video' && (
                        <div className="publish-view__file-preview">
                          <MediaPreview type="video" url={v.content} maxHeight="180px" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {runError && (
              <div className="publish-view__alert publish-view__alert--error">
                <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                <span>{runError}</span>
              </div>
            )}

            {partialWarning && (
              <div className="publish-view__alert publish-view__alert--warning">
                <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                <span>{partialWarning}</span>
              </div>
            )}
          </div>

          {/* Run controls — sticky at the bottom of the inputs panel */}
          <footer className="publish-view__run-bar">
            {/* Cost + balance line */}
            <div className="publish-view__cost-line">
              <div className="publish-view__cost">
                <span className="publish-view__cost-label">Cost</span>
                <span className="publish-view__cost-value">
                  {costPerRun > 0 ? (
                    <>
                      ~{(costPerRun * runCount).toLocaleString()}
                      <span className="publish-view__cost-unit"> credits</span>
                    </>
                  ) : (
                    <span className="publish-view__cost-unit">free</span>
                  )}
                </span>
              </div>
              <div className="publish-view__cost">
                <span className="publish-view__cost-label">Balance</span>
                <span
                  className={`publish-view__cost-value ${
                    credits && credits.balance < costPerRun * runCount
                      ? 'publish-view__cost-value--low'
                      : ''
                  }`}
                >
                  {credits ? credits.balance.toLocaleString() : '—'}
                  <span className="publish-view__cost-unit"> credits</span>
                </span>
              </div>
            </div>

            {/* Stepper + Run button */}
            <div className="publish-view__run-controls">
              <div className="publish-view__stepper" role="group" aria-label="Number of runs">
                <IconButton
                  size="small"
                  onClick={() => setRunCount((c) => Math.max(1, c - 1))}
                  disabled={runCount <= 1}
                  aria-label="Decrease run count"
                  sx={{ color: 'var(--color-text-secondary)' }}
                >
                  <RemoveIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <span className="publish-view__stepper-value" aria-live="polite">
                  ×{runCount}
                </span>
                <IconButton
                  size="small"
                  onClick={() => setRunCount((c) => Math.min(MAX_BATCH, c + 1))}
                  disabled={runCount >= MAX_BATCH}
                  aria-label="Increase run count"
                  sx={{ color: 'var(--color-text-secondary)' }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </div>
              <button
                type="button"
                className="publish-view__btn publish-view__btn--primary publish-view__run-btn"
                disabled={!canSubmit}
                onClick={handleRun}
              >
                <PlayArrowIcon sx={{ fontSize: 18 }} />
                <span>Run</span>
                {runCount > 1 && <span className="publish-view__run-count-badge">×{runCount}</span>}
              </button>
            </div>
          </footer>
        </section>

        {/* ─────────────────────── OUTPUTS PANEL ─────────────────────── */}
        <section className="publish-view__panel publish-view__panel--outputs">
          <header className="publish-view__panel-header">
            <h2 className="publish-view__panel-title">
              Outputs
              {completedCount > 0 && (
                <span className="publish-view__panel-count">{completedCount}</span>
              )}
            </h2>
            <div className="publish-view__panel-header-right">
              {isRunning && (
                <span className="publish-view__running-pill">
                  <CircularProgress size={12} sx={{ color: 'var(--color-accent-primary)' }} />
                  Running {inFlightRuns.length}
                </span>
              )}
              {completedCount > 0 && !isRunning && (
                <>
                  <button
                    type="button"
                    className="publish-view__btn publish-view__btn--ghost publish-view__btn--small"
                    onClick={downloadAll}
                    disabled={isZipping}
                    title="Download all media outputs as a ZIP"
                  >
                    {isZipping ? (
                      <CircularProgress size={14} sx={{ color: 'var(--color-accent-primary)' }} />
                    ) : (
                      <DownloadIcon sx={{ fontSize: 16 }} />
                    )}
                    <span>
                      {isZipping
                        ? `Zipping (${zipProgress!.done}/${zipProgress!.total})`
                        : 'Download all'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="publish-view__btn publish-view__btn--ghost publish-view__btn--small"
                    onClick={handleClearHistory}
                    disabled={isZipping}
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
          </header>

          <div className="publish-view__panel-body publish-view__outputs-body">
            {runHistory.length === 0 && !isRunning ? (
              <div className="publish-view__outputs-empty">
                <p>Outputs will appear here.</p>
                <p className="publish-view__hint">Fill the inputs on the left and hit Run.</p>
              </div>
            ) : (
              <div className="publish-view__grid">
                {/* Skeleton tiles for in-flight runs. With progressive reveal,
                    a single run produces real tiles incrementally; render only
                    as many skeletons as outputs are still pending for that run
                    (expected − arrived), and none once all have arrived. */}
                {inFlightRuns.flatMap((r) => {
                  const arrived = runHistory.filter((t) => t.runId === r.runId).length;
                  const pending = Math.max(0, r.expectedOutputs - arrived);
                  return Array.from({ length: pending }, (_, i) => (
                    <div
                      key={`skeleton-${r.runId}-${i}`}
                      className="publish-view__tile publish-view__tile--skeleton"
                    >
                      <CircularProgress size={28} sx={{ color: 'var(--color-accent-primary)' }} />
                      <div className="publish-view__tile-skeleton-text">
                        {r.progress
                          ? `${r.progress.completed}/${r.progress.total}`
                          : 'Starting…'}
                      </div>
                    </div>
                  ));
                })}

                {/* Completed tiles */}
                {runHistory.map((tile) => {
                  const isMedia = (tile.type === 'image' || tile.type === 'video' || tile.type === 'audio') && !!tile.url;
                  const isDownloadable = (tile.type === 'image' || tile.type === 'video') && !!tile.url;
                  return (
                    <div
                      key={tile.id}
                      role={isMedia ? 'button' : undefined}
                      tabIndex={isMedia ? 0 : undefined}
                      className={`publish-view__tile publish-view__tile--${tile.type}${!isMedia ? ' publish-view__tile--disabled' : ''}`}
                      onClick={() => isMedia && openLightbox(tile.id)}
                      onKeyDown={(e) => {
                        if (isMedia && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          openLightbox(tile.id);
                        }
                      }}
                      title={tile.label}
                    >
                      {isMedia && (
                        <div className="publish-view__tile-actions">
                          {canSubmit && (
                            <button
                              type="button"
                              className="publish-view__tile-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRegenerate(tile.label);
                              }}
                              title={`Regenerate ${tile.label}`}
                              aria-label={`Regenerate ${tile.label}`}
                              disabled={isRunning}
                            >
                              <RefreshIcon sx={{ fontSize: 16 }} />
                            </button>
                          )}
                          {isDownloadable && (
                            <button
                              type="button"
                              className="publish-view__tile-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadTile(tile);
                              }}
                              title={`Download ${tile.label}`}
                              aria-label={`Download ${tile.label}`}
                            >
                              <DownloadIcon sx={{ fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      )}
                      {tile.type === 'image' && tile.url && (
                        <img
                          src={tile.url}
                          alt={tile.label}
                          className="publish-view__tile-media"
                          loading="lazy"
                        />
                      )}
                      {tile.type === 'video' && tile.url && (
                        <video
                          src={tile.url}
                          className="publish-view__tile-media"
                          muted
                          playsInline
                          preload="metadata"
                        />
                      )}
                      {tile.type === 'text' && (
                        <div className="publish-view__tile-text">
                          {tile.text || '(empty)'}
                        </div>
                      )}
                      {tile.type === 'unknown' && (
                        <div className="publish-view__tile-text">
                          {JSON.stringify(tile.raw).slice(0, 200)}
                        </div>
                      )}
                      <div className="publish-view__tile-label">{tile.label}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Fullscreen lightbox — reuse the editor's component, read-only mode */}
      {lightboxOpen && (
        <Lightbox
          imageUrl={galleryTiles[lightboxIndex!].url || ''}
          contentType={galleryTiles[lightboxIndex!].type as 'image' | 'video' | 'audio' | 'text'}
          title={galleryTiles[lightboxIndex!].label}
          onClose={closeLightbox}
          onPrev={lightboxPrev}
          onNext={lightboxNext}
          hasPrev={lightboxIndex! > 0}
          hasNext={lightboxIndex! < galleryTiles.length - 1}
          onDownload={() => downloadTile(galleryTiles[lightboxIndex!])}
        />
      )}
    </div>
  );
};

export default PublishViewPage;
